import { Vector3, type Web3DViewer } from '@technology/web3d';
import type { PointIcon } from '@technology/web3d-plugin-icons';
import { getLogger } from '@trimble/logging';
import { arePointsEqual, type Point3D } from '@trimble/utilities';
import {
  CapabilityCoordinatorBase,
  type CapabilityRegistry,
} from '@trimble/web3d';
import { batch, createEffect, createRoot, createSignal } from 'solid-js';
import { configReady, getMarkerIconPath, iconsVersion } from '../constants';
import { resolveMarkerHashId } from '../utils';
import { getIssueMarkersPlugin } from '../utils/issue-marker-sprite';
import { IssueMarkersPlugin } from '../utils/issue-markers-plugin';
import {
  type ApplyInput,
  type IssueMarkerIconCoordinatorOptions,
  type MarkerInfo,
  type PreviewMarkerVisuals,
} from './issue-marker-icon-coordinator.types';
import { defaultPaneEligibility, getEligibleViewers } from './pane-eligibility';

const PREVIEW_MARKER_ICON_ID = -1;
const MARKER_ICON_SIZE = 32;
/** Max distance (metres) to treat a persisted marker as replacing the preview. */
const PREVIEW_MATCH_EPSILON = 1e-3;

/**
 * Manages issue marker icons across eligible Web3D viewers:
 * a single preview marker during placement and the full list of persisted
 * topic markers.
 */
export class IssueMarkerIconCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private readonly isPaneEligible: NonNullable<
    IssueMarkerIconCoordinatorOptions['isPaneEligible']
  >;

  private readonly getActiveMarkerPoint: () => Readonly<Point3D> | null;

  private readonly updateActiveMarkerPoint: (
    point: Readonly<Point3D> | null
  ) => void;

  private readonly updatePreviewMarkerVisuals: (
    visuals: PreviewMarkerVisuals | null
  ) => void;

  private readonly updateListMarkers: (markers: readonly MarkerInfo[]) => void;

  private readonly updateSelectedMarkerId: (id: string | null) => void;

  private readonly latestJobs = new WeakMap<Web3DViewer, Promise<void>>();

  private readonly pendingJobs = new Map<Web3DViewer, number>();

  /** Viewers that still own sprites or have queued work. Strong refs until remove succeeds. */
  private readonly trackedViewers = new Set<Web3DViewer>();

  private previousViewers: Web3DViewer[] = [];
  private pendingApply: ApplyInput | undefined;
  private applyScheduled = false;
  private applyGeneration = 0;
  private disposeRoot: (() => void) | undefined;

  constructor(
    registry: CapabilityRegistry,
    options: IssueMarkerIconCoordinatorOptions = {}
  ) {
    super(registry);
    this.isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;

    const [activeMarkerPoint, updateActiveMarkerPoint] =
      createSignal<Readonly<Point3D> | null>(null);
    this.getActiveMarkerPoint = activeMarkerPoint;
    this.updateActiveMarkerPoint = updateActiveMarkerPoint;

    const [previewMarkerVisuals, updatePreviewMarkerVisuals] =
      createSignal<PreviewMarkerVisuals | null>(null);
    this.updatePreviewMarkerVisuals = updatePreviewMarkerVisuals;

    const [listMarkers, updateListMarkers] = createSignal<
      readonly MarkerInfo[]
    >([]);
    this.updateListMarkers = updateListMarkers;

    const [selectedMarkerId, updateSelectedMarkerId] = createSignal<
      string | null
    >(null);
    this.updateSelectedMarkerId = updateSelectedMarkerId;

    this.disposeRoot = createRoot((dispose) => {
      createEffect(() => {
        const ready = configReady();
        void iconsVersion();
        const previewPoint = activeMarkerPoint();
        const previewVisuals = previewMarkerVisuals();
        const markers = listMarkers();
        const selectedId = selectedMarkerId();
        const viewers = this.getEligibleViewers();

        this.scheduleApply({
          previewPoint,
          previewVisuals,
          markers: ready ? markers : [],
          selectedId,
          viewers,
        });
      });

      return dispose;
    });
  }

  public showMarker(
    point: Readonly<Point3D>,
    visuals?: { markerType?: string; priority?: string }
  ): void {
    batch(() => {
      this.updatePreviewMarkerVisuals(visuals ?? null);
      this.updateActiveMarkerPoint(point);
    });
  }

  public removeMarker(): void {
    batch(() => {
      this.updateActiveMarkerPoint(null);
      this.updatePreviewMarkerVisuals(null);
    });
  }

  /**
   * @intent Clear active preview when persisted marker reaches the same point,
   * then update list markers so preview and persisted icons do not coexist.
   */
  public setMarkers(markers: readonly MarkerInfo[]): void {
    batch(() => {
      const preview = this.getActiveMarkerPoint();
      if (
        preview &&
        markers.some((marker) =>
          arePointsEqual(marker.point, preview, PREVIEW_MATCH_EPSILON)
        )
      ) {
        this.removeMarker();
      }
      this.updateListMarkers(markers);
    });
  }

  public setSelectedMarkerId(id: string | null): void {
    this.updateSelectedMarkerId(id);
  }

  public async cleanup(): Promise<void> {
    this.applyGeneration += 1;
    this.pendingApply = undefined;

    this.disposeRoot?.();
    this.disposeRoot = undefined;

    const viewers = this.collectKnownViewers();
    for (const viewer of viewers) {
      this.enqueueViewerJob(viewer, []);
    }

    await Promise.all(
      viewers
        .map((viewer) => this.latestJobs.get(viewer))
        .filter((promise): promise is Promise<void> => promise !== undefined)
    );

    this.previousViewers = [];
  }

  private getEligibleViewers(): Web3DViewer[] {
    return getEligibleViewers(this.registry, this.isPaneEligible);
  }

  /**
   * @intent Every viewer that may still own sprites: one we still track after a
   * failed remove, one with queued work, one from the last apply, or one whose
   * pane is eligible before the first apply.
   */
  private collectKnownViewers(): Web3DViewer[] {
    return uniqueViewers([
      ...this.trackedViewers,
      ...this.pendingJobs.keys(),
      ...this.previousViewers,
      ...this.getEligibleViewers(),
    ]);
  }

  private scheduleApply(input: ApplyInput): void {
    this.pendingApply = input;
    if (this.applyScheduled) {
      return;
    }

    this.applyScheduled = true;
    queueMicrotask(() => {
      this.applyScheduled = false;
      const next = this.pendingApply;
      this.pendingApply = undefined;
      if (next) {
        void this.applyDesiredState(next);
      }
    });
  }

  private async applyDesiredState(input: ApplyInput): Promise<void> {
    const generation = ++this.applyGeneration;

    try {
      const desired = await this.buildDesiredIcons(input, generation);
      if (desired === undefined) {
        return;
      }
      if (generation !== this.applyGeneration) {
        return;
      }

      const currentViewers = uniqueViewers(input.viewers);
      const current = new Set(currentViewers);
      const staleViewers = this.previousViewers.filter(
        (viewer) => !current.has(viewer)
      );
      this.previousViewers = currentViewers;

      for (const viewer of currentViewers) {
        this.enqueueViewerJob(viewer, desired);
      }
      for (const viewer of staleViewers) {
        this.enqueueViewerJob(viewer, []);
      }
    } catch (error: unknown) {
      getLogger().warn('Failed to apply issue marker icons', {
        error,
        phase: 'build',
      });
    }
  }

  private async buildDesiredIcons(
    input: ApplyInput,
    generation: number
  ): Promise<PointIcon[] | undefined> {
    const desired: PointIcon[] = [];
    const previewPoint = input.previewPoint;

    if (previewPoint) {
      const iconPath = await getMarkerIconPath({
        markerType: input.previewVisuals?.markerType,
        priority: input.previewVisuals?.priority,
        isPending: true,
      });
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      desired.push({
        id: PREVIEW_MARKER_ICON_ID,
        iconPath,
        position: new Vector3(previewPoint.x, previewPoint.y, previewPoint.z),
        size: MARKER_ICON_SIZE,
      });
    }

    const hashMap = new Map<number, string>();
    for (const marker of input.markers) {
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      const hashId = resolveMarkerHashId(marker.issueId, hashMap);
      if (hashId === -1) {
        continue;
      }
      hashMap.set(hashId, marker.issueId);
      const iconPath = await getMarkerIconPath({
        markerType: marker.type,
        priority: marker.priority,
        isSelected: marker.issueId === input.selectedId,
      });
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      desired.push({
        id: hashId,
        iconPath,
        position: new Vector3(marker.point.x, marker.point.y, marker.point.z),
        size: MARKER_ICON_SIZE,
      });
    }

    return desired;
  }

  /**
   * @intent The plugin orders jobs per viewer and drops a job once a newer one
   * for this coordinator is queued, so an empty job (pane removal or cleanup)
   * is only skipped when a later job replaces the same sprites.
   */
  private enqueueViewerJob(
    viewer: Web3DViewer,
    desired: readonly PointIcon[]
  ): void {
    const plugin =
      desired.length > 0
        ? getIssueMarkersPlugin(viewer)
        : viewer.plugins.get(IssueMarkersPlugin);
    if (!plugin) {
      return;
    }

    this.trackedViewers.add(viewer);
    this.pendingJobs.set(viewer, (this.pendingJobs.get(viewer) ?? 0) + 1);

    const job = plugin
      .setIcons(this, desired)
      .catch((error: unknown) => {
        getLogger().warn('Failed to apply issue marker icons', {
          error,
          phase: 'viewer',
        });
      })
      .finally(() => {
        const remaining = (this.pendingJobs.get(viewer) ?? 1) - 1;
        if (remaining > 0) {
          this.pendingJobs.set(viewer, remaining);
        } else {
          this.pendingJobs.delete(viewer);
          if (!plugin.hasIcons(this)) {
            this.trackedViewers.delete(viewer);
          }
        }
      });
    this.latestJobs.set(viewer, job);
  }
}

function uniqueViewers(viewers: readonly Web3DViewer[]): Web3DViewer[] {
  return [...new Set(viewers)];
}
